import React, { createContext, useContext, useState, useEffect } from 'react';
import { CacheSettings, CacheContextType } from '../types/cache';

const DEFAULT_CACHE_SETTINGS: CacheSettings = {
  enabled: false,
  duration: 24,
  types: {
    funds: false,
    stocks: false,
    analytics: false,
    preferences: false
  }
};

const CacheContext = createContext<CacheContextType | undefined>(undefined);

export function CacheProvider({ children }: { children: React.ReactNode }) {
  const [cacheSettings, setCacheSettings] = useState<CacheSettings>(() => {
    const saved = localStorage.getItem('cache_settings');
    return saved ? JSON.parse(saved) : DEFAULT_CACHE_SETTINGS;
  });

  const [cacheData, setCacheData] = useState<Record<string, { timestamp: number; data: unknown }>>({});

  useEffect(() => {
    localStorage.setItem('cache_settings', JSON.stringify(cacheSettings));
  }, [cacheSettings]);

  const updateCacheSettings = (settings: Partial<CacheSettings>) => {
    setCacheSettings(prev => ({
      ...prev,
      ...settings,
      types: {
        ...prev.types,
        ...(settings.types || {})
      }
    }));
  };

  const isDataValid = (timestamp: number) => {
    if (!cacheSettings.enabled) return false;
    const now = Date.now();
    const expiryTime = timestamp + (cacheSettings.duration * 60 * 60 * 1000);
    return now < expiryTime;
  };

  const getCachedData = <T,>(key: string): T | null => {
    const cached = cacheData[key];
    if (!cached || !isDataValid(cached.timestamp)) {
      return null;
    }
    return cached.data as T;
  };

  const setCachedData = <T,>(key: string, data: T) => {
    if (!cacheSettings.enabled) return;
    setCacheData(prev => ({
      ...prev,
      [key]: {
        timestamp: Date.now(),
        data
      }
    }));
  };

  const clearCache = () => {
    setCacheData({});
  };

  return (
    <CacheContext.Provider
      value={{
        cacheSettings,
        updateCacheSettings,
        getCachedData,
        setCachedData,
        clearCache
      }}
    >
      {children}
    </CacheContext.Provider>
  );

}
export function useCache() {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
}
